import { API_UPLOAD_SALARY, baseURL } from "../../../config/Endpoints.js";
import { postApi } from "../../../utils/api.js";

type SalaryUploadRequest = {
  file: File;
  month: string;
  employeeType: string;
  isSqe: boolean;
  accessToken?: string;
};

export function sqeSalaryUploadUrl(month: string): string {
  const params = new URLSearchParams({
    month: month.toUpperCase(),
    year: String(new Date().getFullYear()),
  });
  return `${baseURL}/salary/sqe/salaryupload?${params.toString()}`;
}

export function uploadSalaryFile(request: SalaryUploadRequest) {
  const body = new FormData();
  body.append("excelFile", request.file);
  const url = request.isSqe
    ? sqeSalaryUploadUrl(request.month)
    : `${API_UPLOAD_SALARY}?employeeType=${request.employeeType}&month=${request.month.toLowerCase()}`;
  return postApi(url, body, request.accessToken);
}

export type SalaryUploadFailure = {
  rowNumber: number | null;
  employeeId: number | null;
  message: string;
};

export type SalaryUploadResult = {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  failures: SalaryUploadFailure[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function numberOrZero(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export function parseSalaryUploadResult(value: unknown): SalaryUploadResult | null {
  let body = asRecord(value);
  if (body?.response) body = asRecord(asRecord(body.response)?.data) ?? body;
  for (let depth = 0; depth < 3 && body && !Array.isArray(body.results); depth += 1) {
    body = asRecord(body.data);
  }
  if (!body || !Array.isArray(body.results)) return null;

  const failures = body.results.flatMap((item): SalaryUploadFailure[] => {
    const row = asRecord(item);
    if (String(row?.status ?? "").toUpperCase() !== "FAILED") return [];
    return [{
      rowNumber: typeof row?.rowNumber === "number" ? row.rowNumber : null,
      employeeId: typeof row?.employeeId === "number" ? row.employeeId : null,
      message: typeof row?.message === "string" && row.message.trim()
        ? row.message
        : "Upload failed for this row.",
    }];
  });

  return {
    totalRows: Math.max(numberOrZero(body.totalRows), body.results.length),
    created: numberOrZero(body.created),
    updated: numberOrZero(body.updated),
    failed: Math.max(numberOrZero(body.failed), failures.length),
    failures,
  };
}

export function salaryUploadErrorMessage(error: unknown): string {
  const response = asRecord(asRecord(error)?.response);
  const body = asRecord(response?.data);
  const message = body?.message ?? asRecord(body?.data)?.message;
  return typeof message === "string" && message.trim()
    ? message
    : "Something went wrong. Please try again!";
}
